<template>
  <div class="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-2xl mx-auto">
      <!-- Loading State -->
      <div v-if="loading" class="text-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
        <p class="text-gray-600">Chargement...</p>
      </div>

      <!-- Success Header -->
      <div v-else-if="uploadData" class="text-center mb-8">
        <div class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
          <svg class="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 class="text-3xl font-bold text-gray-900 mb-2">
          Fichier envoyé avec succès !
        </h1>
        <p class="text-lg text-gray-600">
          Votre fichier a été uploadé et est prêt à être partagé
        </p>
      </div>

      <!-- File Details Card -->
      <div v-if="uploadData" class="card p-8 mb-6">
        <div class="space-y-4">
          <div class="flex items-center space-x-3">
            <svg class="h-8 w-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <div>
              <h3 class="text-lg font-semibold text-gray-900">{{ uploadData.fileName }}</h3>
              <p class="text-sm text-gray-500">{{ formatFileSize(uploadData.fileSize) }}</p>
            </div>
          </div>

          <div class="border-t pt-4">
            <div class="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span class="text-gray-500">Destinataire:</span>
                <p class="font-medium">{{ uploadData.recipientEmail }}</p>
              </div>
              <div>
                <span class="text-gray-500">Expire le:</span>
                <p class="font-medium">{{ formatDate(uploadData.expirationDate) }}</p>
              </div>
            </div>
          </div>

          <div v-if="uploadData.message" class="border-t pt-4">
            <span class="text-gray-500 text-sm">Message:</span>
            <p class="mt-1 text-gray-900">{{ uploadData.message }}</p>
          </div>
        </div>
      </div>

      <!-- Download Link Card -->
      <div v-if="uploadData" class="card p-6 mb-6">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">Lien de téléchargement</h3>
        
        <div class="flex space-x-2">
          <input
            ref="downloadLink"
            :value="uploadData.downloadUrl"
            readonly
            class="flex-1 input-field bg-gray-50"
          />
          <button
            @click="copyLink"
            class="btn-secondary whitespace-nowrap"
          >
            {{ copied ? 'Copié !' : 'Copier' }}
          </button>
        </div>
      </div>

      <!-- Email Notification -->
      <div v-if="uploadData" class="card p-6 mb-6">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">Notification par email</h3>
        
        <div v-if="!emailSent" class="space-y-4">
          <p class="text-gray-600">
            Envoyer une notification par email au destinataire ?
          </p>
          
          <div class="flex space-x-3">
            <button
              @click="sendEmail"
              :disabled="sendingEmail"
              class="btn-primary disabled:opacity-50"
            >
              <span v-if="sendingEmail" class="flex items-center">
                <svg class="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Envoi...
              </span>
              <span v-else>Envoyer l'email</span>
            </button>
            
            <button
              @click="skipEmail"
              class="btn-secondary"
            >
              Plus tard
            </button>
          </div>
        </div>

        <div v-else class="text-center">
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-3">
            <svg class="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p class="text-green-800 font-medium">Email envoyé avec succès !</p>
          <p class="text-sm text-gray-600 mt-1">
            Le destinataire a été notifié par email
          </p>
        </div>
      </div>

      <!-- Actions -->
      <div v-if="uploadData" class="text-center space-y-4">
        <button
          @click="uploadAnother"
          class="btn-primary"
        >
          Envoyer un autre fichier
        </button>
        
        <div class="text-sm text-gray-500">
          <p>Le lien de téléchargement restera actif jusqu'au {{ formatDate(uploadData.expirationDate) }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from '../config/axios'

export default {
  name: 'ConfirmPage',
  data() {
    return {
      uploadData: null,
      copied: false,
      emailSent: false,
      sendingEmail: false,
      loading: true
    }
  },
  mounted() {
    this.loadUploadData()
  },
  methods: {
    loadUploadData() {
      console.log('🔍 [FRONTEND] Chargement des données depuis localStorage');
      const data = localStorage.getItem('uploadData')
      console.log('🔍 [FRONTEND] Raw localStorage data:', data)
      
      if (data) {
        try {
          this.uploadData = JSON.parse(data)
          console.log('🔍 [FRONTEND] Parsed upload data:', this.uploadData)
          console.log('📁 [FRONTEND] Nom du fichier dans les données:', `"${this.uploadData.fileName}"`);
        } catch (error) {
          console.error('❌ [FRONTEND] Error parsing upload data:', error)
          this.$router.push('/')
        }
      } else {
        console.log('❌ [FRONTEND] No upload data found in localStorage')
        // Redirect to upload page if no data
        this.$router.push('/')
      }
      this.loading = false
    },
    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    formatDate(dateString) {
      if (!dateString) return 'Date inconnue'
      try {
        return new Date(dateString).toLocaleDateString('fr-FR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (error) {
        return 'Date invalide'
      }
    },
    async copyLink() {
      if (!this.uploadData) return
      
      try {
        await navigator.clipboard.writeText(this.uploadData.downloadUrl)
        this.copied = true
        setTimeout(() => {
          this.copied = false
        }, 2000)
      } catch (error) {
        // Fallback for older browsers
        this.$refs.downloadLink.select()
        document.execCommand('copy')
        this.copied = true
        setTimeout(() => {
          this.copied = false
        }, 2000)
      }
    },
    async sendEmail() {
      if (!this.uploadData) return
      
      this.sendingEmail = true
      
      try {
        console.log('Sending email with data:', {
          fileId: this.uploadData.fileId,
          recipientEmail: this.uploadData.recipientEmail,
          message: this.uploadData.message
        })
        
        await apiClient.post('/email/send-notification', {
          fileId: this.uploadData.fileId,
          recipientEmail: this.uploadData.recipientEmail,
          message: this.uploadData.message
        })
        
        this.emailSent = true
      } catch (error) {
        console.error('Email error:', error)
        console.error('Error response:', error.response?.data)
        alert('Erreur lors de l\'envoi de l\'email: ' + (error.response?.data?.error || error.message))
      } finally {
        this.sendingEmail = false
      }
    },
    skipEmail() {
      this.emailSent = true
    },
    uploadAnother() {
      localStorage.removeItem('uploadData')
      this.$router.push('/')
    }
  }
}
</script> 